import { z } from 'zod';
import { HomeSlot, LocationId, PetSpecies, SkillId } from './enums';
import { ShiftPackSource } from './shift';
import { GrowthDelta, WorkDay } from './story';

// ---------- careers (P5) ----------


export const CareerLevelView = z.object({ level: z.number().int(), title: z.string(), payCoins: z.number().int(), unlocked: z.boolean(), lockedReason: z.string().nullable() });
export const CareerView = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  locationId: LocationId,
  /** Open once its unlock conditions are met (each listed and ticked); before the first shift it sits at Lv.1. */
  unlocked: z.boolean(),
  requirements: z.array(z.object({ label: z.string(), met: z.boolean() })),
  level: z.number().int(),
  xp: z.number().int(),
  xpToNext: z.number().int().nullable(),
  levels: z.array(CareerLevelView),
  /** Who you work with, whether today's shift is done, and how soon something happens at work. */
  coworker: z.object({ npcId: z.string(), name: z.string(), portraitUrl: z.string() }),
  /** The workplace right now (the shift mini-game's stage). */
  backgroundUrl: z.string(),
  workedToday: z.boolean(),
  shiftsUntilScene: z.number().int(),
});
export type CareerView = z.infer<typeof CareerView>;
/** Every job, plus today's shifts across all of them. */
export const CareersView = z.object({
  jobs: z.array(CareerView),
  today: WorkDay,
});
export type CareersView = z.infer<typeof CareersView>;
/** A shift's grade from its mini-game score; null when the shift was just worked. */
export const ShiftGrade = z.enum(['S', 'A', 'B', 'C']);
export type ShiftGrade = z.infer<typeof ShiftGrade>;
export const shiftGrade = (score: number | null | undefined): ShiftGrade | null =>
  score === null || score === undefined ? null : score >= 90 ? 'S' : score >= 70 ? 'A' : score >= 40 ? 'B' : 'C';

/** 上班紀錄: totals, each job's shifts and best grade, and the latest shifts. */
export const WorkHistory = z.object({
  totals: z.object({ shifts: z.number().int(), coins: z.number().int() }),
  jobs: z.array(z.object({ careerId: z.string(), name: z.string(), shifts: z.number().int(), best: ShiftGrade.nullable() })),
  recent: z.array(
    z.object({
      id: z.string(),
      jobName: z.string(),
      levelTitle: z.string(),
      gameDay: z.number().int(),
      grade: ShiftGrade.nullable(),
      coins: z.number().int(),
      bonusCoins: z.number().int(),
      extra: z.boolean(),
      promoted: z.boolean(),
    }),
  ),
});
export type WorkHistory = z.infer<typeof WorkHistory>;

/** How the shift mini-game went (0–100); leave it out to just work the shift at base pay. */
export const WorkShiftRequest = z.object({
  score: z.number().int().min(0).max(100).optional(),
  /** Pay EXTRA_SHIFTS.coins for a shift past the day's free ones. */
  extra: z.boolean().optional(),
  /**
   * The pack the shift was played from (ShiftPackView.source): who was served is read from that
   * same pack, even if the AI one landed in between. Left out, the server uses today's current pack.
   */
  packSource: ShiftPackSource.optional(),
});
export type WorkShiftRequest = z.infer<typeof WorkShiftRequest>;
export const WorkShiftResult = z.object({
  narration: z.string(),
  growth: GrowthDelta,
  careerLevelUp: z.boolean(),
  /** Extra coins from playing the shift well (tips). */
  bonusCoins: z.number().int(),
  /** The new title when this shift earned a promotion. */
  levelTitle: z.string().nullable(),
  /** 班次小劇場: a short scene with your coworker, when this shift has one ready. */
  scene: z.object({ templateKey: z.string(), title: z.string(), coworkerName: z.string() }).nullable(),
  /** How the shift went (null when it was just worked), and who you worked with, for the reaction line. */
  grade: ShiftGrade.nullable(),
  coworker: z.object({ name: z.string(), portraitUrl: z.string() }),
});
export type WorkShiftResult = z.infer<typeof WorkShiftResult>;

// ---------- home (P5) ----------

export const FurnitureView = z.object({ id: z.string(), name: z.string(), slot: HomeSlot, coinPrice: z.number().int(), purchasable: z.boolean(), owned: z.boolean(), placed: z.boolean(), imageUrl: z.string() });
export type FurnitureView = z.infer<typeof FurnitureView>;
export const HomeView = z.object({
  ownerName: z.string(),
  /** The room's light, from the owner's clock: the room and every layer are painted for it. */
  light: z.enum(['day', 'evening']),
  roomImageUrl: z.string(),
  /** `imageUrl` is the piece's room layer: its slot box of the room (ROOM_SLOT_BOX), transparent where the room shows. */
  placed: z.array(z.object({ slot: HomeSlot, furnitureId: z.string(), imageUrl: z.string(), name: z.string() })),
  pet: z.object({ species: PetSpecies, name: z.string(), imageUrl: z.string() }).nullable(),
  furniture: z.array(FurnitureView),
});
export type HomeView = z.infer<typeof HomeView>;
export const PlaceFurnitureRequest = z.object({ slot: HomeSlot, furnitureId: z.string().max(40).nullable() });
export type PlaceFurnitureRequest = z.infer<typeof PlaceFurnitureRequest>;
export const HomeNoteRequest = z.object({ clientMessageId: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/), body: z.string().trim().min(1).max(200) });
export type HomeNoteRequest = z.infer<typeof HomeNoteRequest>;

// ---------- pets (P5) ----------

export const PetOption = z.object({ species: PetSpecies, name: z.string(), description: z.string(), coinPrice: z.number().int(), imageUrl: z.string() });
export const PetView = z.object({
  pet: z.object({ species: PetSpecies, name: z.string(), affinity: z.number().int(), adoptedDay: z.number().int(), imageUrl: z.string(), happyImageUrl: z.string() }).nullable(),
  options: z.array(PetOption),
  caredToday: z.boolean(),
});
export type PetView = z.infer<typeof PetView>;
export const AdoptPetRequest = z.object({ species: PetSpecies, name: z.string().trim().min(1).max(12) });
export type AdoptPetRequest = z.infer<typeof AdoptPetRequest>;
export const PetCareResult = z.object({ narration: z.string(), affinity: z.number().int() });
export type PetCareResult = z.infer<typeof PetCareResult>;

// ---------- breakthroughs (P4) ----------

export const BreakthroughView = z.object({ id: z.string(), skill: SkillId, level: z.number().int(), title: z.string(), description: z.string(), available: z.boolean(), claimed: z.boolean(), rewardCoins: z.number().int() });
export type BreakthroughView = z.infer<typeof BreakthroughView>;

// ---------- manga volumes (P4) ----------

export const VolumeView = z.object({
  id: z.string(),
  kind: z.enum(['custom', 'character', 'shared']),
  title: z.string(),
  /** Character volumes: the chapters this neighbour leads, in the order they were lived. */
  chapterIds: z.array(z.string()),
  /** Character volumes: chapters led by someone else that this neighbour appears in. */
  cameoChapterIds: z.array(z.string()).default([]),
  coverUrl: z.string().nullable(),
  npcId: z.string().nullable(),
  updatedAt: z.string(),
});
export type VolumeView = z.infer<typeof VolumeView>;
export const VolumeUpsertRequest = z.object({ title: z.string().trim().min(1).max(30), chapterIds: z.array(z.string().max(64)).min(1).max(60) });
export type VolumeUpsertRequest = z.infer<typeof VolumeUpsertRequest>;

// ---------- clubs (P5) ----------

export const ClubView = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  code: z.string(),
  ownerUserId: z.string(),
  members: z.array(z.object({ userId: z.string(), name: z.string(), portraitUrl: z.string().nullable(), role: z.enum(['owner', 'member']) })),
  conversationId: z.string(),
  isMember: z.boolean(),
  maxMembers: z.number().int(),
});
export type ClubView = z.infer<typeof ClubView>;
export const CreateClubRequest = z.object({ name: z.string().trim().min(2).max(20), description: z.string().trim().max(120).default('') });
export type CreateClubRequest = z.input<typeof CreateClubRequest>;
export const JoinClubRequest = z.object({ code: z.string().regex(/^[A-Z0-9]{6}$/) });
export type JoinClubRequest = z.infer<typeof JoinClubRequest>;

// ---------- creators (P6) ----------

export const SubmissionView = z.object({
  id: z.string(),
  title: z.string(),
  status: z.enum(['pending', 'approved', 'rejected']),
  reviewNote: z.string().nullable(),
  templateKey: z.string().nullable(),
  tips: z.number().int(),
  plays: z.number().int(),
  createdAt: z.string(),
});
export type SubmissionView = z.infer<typeof SubmissionView>;
export const CommunityStoryView = z.object({ templateKey: z.string(), title: z.string(), synopsis: z.string(), authorName: z.string(), tips: z.number().int(), plays: z.number().int() });
export type CommunityStoryView = z.infer<typeof CommunityStoryView>;
/** Coins one tip may carry. */
export const TIP_COINS = { min: 10, max: 500 } as const;
export const TipRequest = z.object({ coins: z.number().int().min(TIP_COINS.min).max(TIP_COINS.max) });
export type TipRequest = z.infer<typeof TipRequest>;

// ---------- voice (P5 experiment) ----------

export const TtsRequest = z.object({ source: z.enum(['turn', 'message']), id: z.string().max(64), lineIndex: z.number().int().min(0).max(40).default(0) });
export type TtsRequest = z.input<typeof TtsRequest>;
export const TtsResponse = z.object({ audioUrl: z.string(), cached: z.boolean() });
export type TtsResponse = z.infer<typeof TtsResponse>;

/** The simple creator form (P6): the server turns it into a full, safe event template. */
export const CreatorStoryInput = z.object({
  title: z.string().trim().min(2).max(24),
  synopsis: z.string().trim().min(10).max(80),
  location: z.enum(['apartment', 'university', 'cafe', 'park', 'town_square', 'seaside']),
  npcId: z.enum(['mia', 'chloe', 'emma', 'ethan', 'daniel', 'leo']),
  opening: z.string().trim().min(20).max(180),
  npcLine: z.string().trim().min(2).max(80),
  choices: z
    .array(z.object({ label: z.string().trim().min(2).max(30), reaction: z.string().trim().min(2).max(120), ending: z.enum(['warm', 'funny']) }))
    .min(2)
    .max(3)
    .refine((cs) => cs.some((c) => c.ending === 'warm') && cs.some((c) => c.ending === 'funny'), 'use both endings'),
  endings: z.object({
    warm: z.object({ title: z.string().trim().min(2).max(20), closing: z.string().trim().min(10).max(160) }),
    funny: z.object({ title: z.string().trim().min(2).max(20), closing: z.string().trim().min(10).max(160) }),
  }),
});
export type CreatorStoryInput = z.infer<typeof CreatorStoryInput>;
